import { MouseEvent, useCallback, useContext } from 'react'

import { useSSOV2Store } from '@hhgtech/hhg-components/ssoV2'
import { Box, BoxProps } from '@mantine/core'

import { getUrlToRedirectToSSOWithReturnUrl } from 'api/utils/paths'
import { useHandlerUrls } from 'hooks/useHandlerUrls'
import { IS_SSOV2_ENABLED } from 'interfaces/constants'
import { SpotlightClickableSingleBannerSession } from 'interfaces/types'
import { AppContext } from 'state/appContext'

import useStyles from './index.styles'
import { RiskScreenerLayout } from '../riskScreenerLayout'

type ClickableBannerProps = {
  data: SpotlightClickableSingleBannerSession
  onItemClick?: (v: string) => void
  applyH1?: boolean
} & BoxProps

export const ClickableSingleBanner = ({
  data,
  onItemClick,
  styles,
  classNames,
  applyH1,
  ...rest
}: ClickableBannerProps) => {
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const { classes } = useStyles(undefined, {
    name: 'ClickableSingleBanner',
    styles,
    classNames,
  })
  const {
    title,
    description,
    requireLogin,
    singleBanner: bannerInfo,
    disclaimer,
  } = data
  const { icon, banner, url, ctaTitle } = bannerInfo
  const linkData = useHandlerUrls([url])
  const { isExternalUrl } = linkData?.[0] || {}
  const handleClick = useCallback(() => {
    if (!isExternalUrl && url.includes('#')) {
      const splitUrl = url.split('#')
      onItemClick?.(splitUrl[splitUrl.length - 1])
    } else {
      if (requireLogin && !isLoggedIn && IS_SSOV2_ENABLED) {
        useSSOV2Store.getState().triggerLogin({
          returnUrl: url,
        })
      } else {
        window.open(
          requireLogin && !isLoggedIn
            ? getUrlToRedirectToSSOWithReturnUrl(url)
            : url,
        )
      }
    }
  }, [isExternalUrl, isLoggedIn])

  return (
    <RiskScreenerLayout
      applyH1={applyH1}
      title={title}
      description={description}
      url={url}
      ctaTitle={ctaTitle}
      ctaBtnProps={{
        dataEventCategory: 'Service Connection',
        dataEventAction: 'Clickable Banner',
        dataEventLabel: url,
      }}
      ctaFn={handleClick}
      icon={icon}
      styles={styles}
      classNames={classNames}
      disclaimer={{
        heading: disclaimer.title,
        description: disclaimer.description,
      }}
      {...rest}
    >
      <Box
        component="a"
        {...(url
          ? {
              href: url,
              target: isExternalUrl ? '_blank' : '_self',
              onClick: (e: MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault()
                handleClick()
              },
            }
          : {})}
        className={classes.imgHref}
        data-event-category="Service Connection"
        data-event-action="Clickable Banner"
        data-event-label={url}
      >
        <img
          src={banner}
          alt={title}
          loading="lazy"
          className={classes.img}
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
      </Box>
    </RiskScreenerLayout>
  )
}
